<template>
  <div class="app-header" :class="`layout-${layout}`" key="card">
    <div class="header-top">
      <div class="header-branding">
        <!-- Top layout: logo lives here. Side layout: SideNav owns the logo, this shows
             where you are (mirrors the "Originate / Your Pipeline" block in the mockup). -->
        <img v-if="layout === 'top'" src="../assets/SwiftAI logo - 2026.svg" alt="Swift" class="brand-logo" />
        <!-- Side layout: no title of our own. Each view teleports its own existing page-header
             block in here (see <PageHeaderPortal> + components/header.vue "Side layout" CSS
             below), so whatever heading a page already renders just relocates, unchanged. -->
        <div v-else id="header-page-slot" class="header-page-slot"></div>
      </div>

      <div class="header-actions">
        <button
          class="new-app-btn"
          type="button"
          @click="onNewAppClick"
          :disabled="!larLoaded"
        >
          New Application
        </button>

        <ProfileCardMenu
          :azure-id="azureId"
          :token="token?.accessToken"
          :show-impersonate="showImpersonate"
          :impersonating="impersonating"
          :impersonating-name="impersonatingName"
          @open-impersonate="showuserslist"
          @stop-impersonate="stopImpersonate"
          @logout="deepLogout"
        />
      </div>
    </div>

    <div class="header-nav">
      <div class="nav-links">
        <router-link to="/" class="nav-link">Command Center</router-link>

        <router-link to="/Applications" class="nav-link hide-on-mobile">
          Applications
        </router-link>

        <router-link to="/Processing" class="nav-link">Pipeline</router-link>

        <!-- Ungated, its own link rather than a swiftLocks item. This bar has no Pricing Engine
             link, so it goes where the sidebar's order puts it: after the core pages. -->
        <router-link to="/my-locks" class="nav-link hide-on-mobile">myLocks</router-link>

        <router-link to="/swift-prequal" class="nav-link hide-on-mobile">swiftPrequal</router-link>

        <router-link v-if="isPostClosing" to="/PostCloseConnect" class="nav-link hide-on-mobile">
          swiftPC
        </router-link>

        <div v-if="isCorp" class="nav-dropdown hide-on-mobile">
          <span class="nav-link nav-dropdown-trigger"
                :class="{ 'router-link-active': isSwiftUWActive }">
            swiftUW <i class="fa-solid fa-chevron-down nav-chevron"></i>
          </span>
          <div class="nav-dropdown-menu">
            <!-- Mirrors the swiftUW children in navConfig.js, which the sidebar renders. -->
            <router-link to="/Insights" class="nav-dropdown-item">Condition Insights</router-link>
            <router-link to="/AppraisalReview" class="nav-dropdown-item">Appraisal Review</router-link>
          </div>
        </div>

        <router-link v-if="isAdmin" to="/swift-analyze" class="nav-link hide-on-mobile">
          swiftAnalyze
        </router-link>

        <!-- PA Import moved to SWIFT SPA (/PurchaseAgreement on the open loan). Hidden here; the route
             still works by URL. Uncomment with navConfig.js to bring it back.
        <router-link v-if="isAdmin" to="/purchase-agreement-import" class="nav-link hide-on-mobile">
          PA Import
        </router-link>
        -->

        <router-link to="/Leaderboard" class="nav-link hide-on-mobile">
          Leaderboard
        </router-link>

        <router-link to="/turn-times" class="nav-link hide-on-mobile">
          Turn Times
        </router-link>

        <div v-if="canViewLocks" class="nav-dropdown hide-on-mobile">
          <span class="nav-link nav-dropdown-trigger"
                :class="{ 'router-link-active': isSwiftLocksActive }">
            swiftLocks <i class="fa-solid fa-chevron-down nav-chevron"></i>
          </span>
          <div class="nav-dropdown-menu">
            <!-- Gated per ITEM, not just per dropdown: a Branch Manager opens this menu for
                 Revenue Analysis alone and must not get the lock desk's pages with it. Mirrors
                 the swiftLocks children in navConfig.js, which the sidebar renders. -->
            <router-link v-if="canViewRepricedLocks" to="/lock-desk-queue" class="nav-dropdown-item">Work Queue</router-link>
            <router-link v-if="canViewRevenueAnalysis" to="/revenue-analysis" class="nav-dropdown-item">Revenue Analysis</router-link>
            <router-link v-if="canViewRepricedLocks" to="/validations" class="nav-dropdown-item">Validations</router-link>
            <router-link v-if="canViewRepricedLocks" to="/reprice" class="nav-dropdown-item">Lock Reprice</router-link>
            <router-link v-if="canViewRepricedLocks" to="/extensions" class="nav-dropdown-item">Lock Extensions</router-link>
            <router-link v-if="canViewRepricedLocks" to="/lock-extension-request" class="nav-dropdown-item">Extension Request</router-link>
            <router-link v-if="canViewRepricedLocks" to="/loan-price-sheet" class="nav-dropdown-item">Price Sheet</router-link>
            <router-link v-if="canViewPricingComparison" to="/pricing-comparison" class="nav-dropdown-item">Pricing Comparison</router-link>
            <!-- Concessions used to be reachable only by typing /concessions. It lives here
                 now, so it inherits this dropdown's canViewRepricedLocks gate. -->
            <router-link v-if="canViewRepricedLocks" to="/concessions" class="nav-dropdown-item">Concessions</router-link>
          </div>
        </div>
        <!-- Insights (ported from CGS2022). The same sections, pages and gates as the sidebar's
             Insights group. Both come from helpers/insights/insightPages.js, and the list is
             empty while the insights flag is off. -->
        <div v-if="insightNavSections.length" class="nav-dropdown hide-on-mobile">
          <span class="nav-link nav-dropdown-trigger"
                :class="{ 'router-link-active': isInsightsActive }">
            Insights <i class="fa-solid fa-chevron-down nav-chevron"></i>
          </span>
          <div class="nav-dropdown-menu">
            <div v-for="sec in insightNavSections" :key="sec.section" class="nav-submenu">
              <span class="nav-dropdown-item nav-submenu-trigger">
                {{ sec.section }} <i class="fa-solid fa-chevron-right nav-submenu-chevron"></i>
              </span>
              <div class="nav-submenu-menu">
                <router-link v-for="p in sec.pages" :key="p.slug" :to="p.to" class="nav-dropdown-item">{{ p.label }}</router-link>
              </div>
            </div>
          </div>
        </div>
        <div v-if="canViewHmda" class="nav-dropdown hide-on-mobile">
          <span class="nav-link nav-dropdown-trigger"
                :class="{ 'router-link-active': isSwiftComplyActive }">
            swiftComply <i class="fa-solid fa-chevron-down nav-chevron"></i>
          </span>
          <div class="nav-dropdown-menu">
                    <div class="nav-submenu">
              <span class="nav-dropdown-item nav-submenu-trigger">
                 Disclosures<i class="fa-solid fa-chevron-right nav-submenu-chevron"></i>
              </span>
              <div class="nav-submenu-menu">
              
                <router-link to="/ICD" class="nav-dropdown-item">ICD</router-link>
              </div>
            </div>
            <!-- ── Loan Status Disclosures ── -->
            <div class="nav-submenu">
              <span class="nav-dropdown-item nav-submenu-trigger">
                 Loan Status  <i class="fa-solid fa-chevron-right nav-submenu-chevron"></i>
              </span>
              <div class="nav-submenu-menu">
                <router-link to="/hmda-withdrawals" class="nav-dropdown-item">Loan Status</router-link>
              
              </div>
            </div>

            <!-- ── Insuring ── -->
            <div class="nav-submenu">
              <span class="nav-dropdown-item nav-submenu-trigger">
                Insuring <i class="fa-solid fa-chevron-right nav-submenu-chevron"></i>
              </span>
              <div class="nav-submenu-menu">
                <router-link to="/ai-insuring" class="nav-dropdown-item">Closing Packages</router-link>
                <router-link to="/Insuring" class="nav-dropdown-item">Upload FHA MIP</router-link>
                <router-link to="/fha-mip-payments" class="nav-dropdown-item">FHA MIP Payments</router-link>
              </div>
            </div>
          </div>
        </div>
      
    

        <div v-if="canViewAccounting" class="nav-dropdown hide-on-mobile">
          <span class="nav-link nav-dropdown-trigger"
                :class="{ 'router-link-active': isSwiftAccountingActive }">
            swiftAccounting <i class="fa-solid fa-chevron-down nav-chevron"></i>
          </span>
          <div class="nav-dropdown-menu">
            <div class="nav-submenu">
              <span class="nav-dropdown-item nav-submenu-trigger">
                Loan Vision <i class="fa-solid fa-chevron-right nav-submenu-chevron"></i>
              </span>
              <div class="nav-submenu-menu">
                <router-link to="/loans-funded" class="nav-dropdown-item">Loans Funded</router-link>
              </div>
            </div>
          </div>
        </div>

        <a
          v-if="isCorp"
          :href="applicationAdminLink"
          target="_blank"
          class="nav-link hide-on-mobile"
        >
          Application Admin
        </a>

        <router-link to="/NotificationManagement" class="nav-link hide-on-mobile">
          Notification Management
        </router-link>
      </div>
    </div>
  </div>

  <!-- Application Type Picker Modal -->
  <transition name="modal-fade">
    <div v-if="showAppTypePicker" class="app-type-overlay" @click.self="showAppTypePicker = false">
      <div class="app-type-modal">
        <div class="app-type-header">
          <h2 class="app-type-title">Start New Application</h2>
          <p class="app-type-subtitle">Select the type of application to begin</p>
          <button class="app-type-close" @click="showAppTypePicker = false">
            <i class="fa-regular fa-xmark"></i>
          </button>
        </div>
        <div class="app-type-options">
          <button class="app-type-card" @click="onSelectRegularApp">
            <div class="app-type-icon">
              <i class="fa-light fa-file-signature"></i>
            </div>
            <div class="app-type-info">
              <p class="app-type-name">Standard</p>
              <p class="app-type-desc">Forward mortgage loan application via swiftApp</p>
            </div>
            <i class="fa-light fa-arrow-right app-type-arrow"></i>
          </button>

          <button class="app-type-card" @click="onSelectReverseMortgage">
            <div class="app-type-icon reverse">
              <i class="fa-light fa-house-chimney"></i>
            </div>
            <div class="app-type-info">
              <p class="app-type-name">Reverse</p>
              <p class="app-type-desc">Initial intake form for submission to the reverse mortgage team</p>
            </div>
            <i class="fa-light fa-arrow-right app-type-arrow"></i>
          </button>

          <button class="app-type-card" @click="onSelectCommercialLoan">
            <div class="app-type-icon commercial">
              <i class="fa-light fa-building-columns"></i>
            </div>
            <div class="app-type-info">
              <p class="app-type-name">Commercial</p>
              <p class="app-type-desc">Commercial loan intake for multifamily, retail, office, industrial &amp; more</p>
            </div>
            <i class="fa-light fa-arrow-right app-type-arrow"></i>
          </button>

          <button class="app-type-card" @click="onPickLoanOfficerApp">
            <div class="app-type-icon loanofficer">
              <i class="fa-light fa-user-tie"></i>
            </div>
            <div class="app-type-info">
              <p class="app-type-name">For Another Loan Officer</p>
              <p class="app-type-desc">Standard application submitted on behalf of a different loan officer</p>
            </div>
            <i class="fa-light fa-arrow-right app-type-arrow"></i>
          </button>
        </div>
      </div>
    </div>
  </transition>

  <Dialog v-if="showHelp" :title="'How to Complete Application'" @close="showHelp = false">
    <div class="help-modal">
      <div v-if="helpFrameLoading" class="iframe-loader">
        <i class="fa-solid fa-spinner fa-spin"></i>
        Loading training video...
      </div>

      <iframe
        class="help-frame"
        src="https://embed.app.guidde.com/playbooks/mXxqZoCQvHE58P3S2dAXqX?mode=videoOnly"
        frameborder="0"
        allowfullscreen
        @load="helpFrameLoading = false"
      ></iframe>
    </div>
  </Dialog>

  <!-- "View as…": swiftMarketing's picker (components/impersonation/ImpersonationDialog.vue). -->
  <impersonation-dialog v-if="showImpersonate" v-model:visible="pickAUser" />

  <Dialog v-if="pickLoanOfficer" :title="'Start Application For'" @close="onCloseLoanOfficerPicker" class="imper">
    <div class="p-4 space-y-4">
      <dropdownlist
        v-if="pickLoanOfficer"
        :data-items="filteredLoanOfficers"
        :value="selectedLoanOfficer"
        @change="onLoanOfficerChange"
        :filterable="true"
        @filterchange="onLoanOfficerFilterChange"
        :text-field="'displayName'"
        :data-item-key="'id'"
        :disabled="filteredLoanOfficers.length === 0 || launchingLoanOfficerApp"
        :loading="loanOfficersLoading || launchingLoanOfficerApp"
        class="task__form__multi"
        placeholder="Search by name..."
        :rounded="'large'"
        :fill-mode="'outline'"
        :label="'Select the loan officer'"
        :style="{ width: '250px' }"
      />

      <div v-if="launchingLoanOfficerApp" class="impersonate-status">
        <i class="fa-solid fa-spinner fa-spin"></i>
        Opening application...
      </div>
    </div>
  </Dialog>

  <gsai-window :visible="showAi" :url="url" @toggleAIDialog="closegsai" />
</template>

<script>
import apiService from "../api/api.service.js"
import { Button } from "@progress/kendo-vue-buttons"
import auth from "../authConfig.js"
import gsai from "./gsAiWindow.vue"
import ProfileCardMenu from "@/components/UserProfileMenu.vue"
import ImpersonationDialog from "@/components/impersonation/ImpersonationDialog.vue"
import { canImpersonate, getActAs, stopImpersonation } from "@/helpers/impersonation.js"
import { DropDownList } from "@progress/kendo-vue-dropdowns"
import { Dialog } from "@progress/kendo-vue-dialogs"
import { filterBy } from "@progress/kendo-data-query"
import bus from "@/helpers/appBus.js"
import { insightSections, insightGates, insightPath, INSIGHTS_BASE } from "@/helpers/insights/insightPages.js"
import { INSIGHT_COMPONENTS } from "@/router/insightRoutes.js"

export default {
  props: {
    // "top" = the existing horizontal nav (default, unchanged). "side" = SideNav.vue renders
    // the links; this component keeps only the top bar (title, New Application, profile) and
    // all the dialogs. On ≤630px the horizontal nav comes back via CSS as the mobile fallback.
    layout: { type: String, default: "top", validator: v => ["top", "side"].includes(v) }
  },
  data() {
    return {
      userRoles: [],
      activeUserRoles: [],
      isAppBeta: false,
      isCorp: false,
      isPostClosing: false,
      canViewRepricedLocks: false,
      canViewRevenueAnalysis: false,
      canViewPricingComparison: false,
      canViewLocks: false,
      canViewHmda: false,
      canViewAccounting: false,
      insightNavSections: [],
      isAdmin: false,
      showAi: false,
      showHelp: false,
      helpFrameLoading: false,
      showAppTypePicker: false,
      url: "",
      lar: "",
      larLoaded: false,
      token: "",
      azureId: "",
      pickAUser: false,
      showImpersonate: false,
      impersonating: false,
      impersonatingName: "",
      pickLoanOfficer: false,
      loanOfficerUsers: [],
      filteredLoanOfficers: [],
      selectedLoanOfficer: null,
      loanOfficersLoading: false,
      launchingLoanOfficerApp: false
    }
  },
  computed: {
    applicationAdminLink() {
      let appSiteUrl = process.env.VUE_APP_LOANLAUNCH_APP_ENDPOINT
      return `${appSiteUrl}/applicationAdmin`
    },
    isSwiftLocksActive() {
      const path = this.$route?.path || ''
      return ['/reprice', '/lock-desk-queue', '/revenue-analysis', '/validations', '/extensions', '/lock-extension-request', '/loan-price-sheet', '/pricing-comparison', '/concessions']
        .some(p => path.startsWith(p))
    },
    isSwiftUWActive() {
      const path = String(this.$route?.path || '').toLowerCase()
      return ['/insights', '/appraisalreview'].some(p => path.startsWith(p))
    },
    isSwiftAccountingActive() {
      const path = this.$route?.path || ''
      return ['/loans-funded'].some(p => path.startsWith(p))
    },
    isInsightsActive() {
      const path = String(this.$route?.path || '').toLowerCase()
      return path === INSIGHTS_BASE || path.startsWith(INSIGHTS_BASE + '/')
    },
    isSwiftComplyActive() {
      const path = this.$route?.path || ''
      return ['/hmda-withdrawals', '/ICD', '/ai-insuring', '/Insuring', '/fha-mip-payments']
        .some(p => path.startsWith(p))
    }
  },
  components: {
    "k-button": Button,
    "gsai-window": gsai,
    dropdownlist: DropDownList,
    Dialog,
    ProfileCardMenu,
    "impersonation-dialog": ImpersonationDialog
  },
  async mounted() {
    this.token = await auth.acquireTokenResponse()
    this.loadContext()
    bus.on("auth-context-ready", this.onAuthContextReady)
    await this.getLar()
  },
  beforeUnmount() {
    bus.off("auth-context-ready", this.onAuthContextReady)
  },
  methods: {
    openHelp() {
      this.helpFrameLoading = true
      this.showHelp = true
    },
    onAuthContextReady() {
      this.loadContext()
      this.getLar()
    },
    loadContext() {
      this.azureId = localStorage.getItem("azure_id") || ""
      const actAs = getActAs()
      this.impersonating = !!actAs
      this.impersonatingName = actAs?.target_name || ""
      this.userRoles = this.getRolesFromStorage("user_roles")
      this.activeUserRoles = this.getRolesFromStorage("active_user_roles")
      // Who may view as whom is the server's call (GSDesignImpersonation, as in swiftMarketing); App.vue asks it.
      this.showImpersonate = canImpersonate()
      this.applyNavPermissions(this.activeUserRoles)
    },
    getRolesFromStorage(key) {
      const raw = localStorage.getItem(key)
      if (!raw) return []

      try {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) {
          return parsed.map(x => String(x || "").trim()).filter(Boolean)
        }

        return String(parsed || "")
          .split(",")
          .map(x => String(x || "").trim())
          .filter(Boolean)
      } catch {
        return String(raw || "")
          .split(",")
          .map(x => String(x || "").trim())
          .filter(Boolean)
      }
    },
    rolesInclude(roles, role) {
      const target = String(role || "").toLowerCase().trim()
      return (roles || [])
        .map(x => String(x || "").toLowerCase().trim())
        .includes(target)
    },
    applyNavPermissions(roles) {
      this.isCorp = this.rolesInclude(roles, "Corp")
      this.isPostClosing =
        this.rolesInclude(roles, "Post Closer") || this.rolesInclude(roles, "Corp")
      this.isAppBeta =
        this.rolesInclude(roles, "APPBETA") || this.rolesInclude(roles, "Corp")
      this.canViewRepricedLocks = this.rolesInclude(roles, "Compensation Admin")
      // A Branch or Division Manager reaches Revenue Analysis and nothing else in swiftLocks.
      // Menu visibility only — the API scopes the data to their branch or their division.
      this.canViewRevenueAnalysis =
        this.canViewRepricedLocks
        || this.rolesInclude(roles, "Branch Manager")
        || this.rolesInclude(roles, "Division Manager")
      // myLocks is its own link now, so the dropdown is back to its gated items. Mirrors navConfig.js.
      // Pricing Comparison has its own role (Compensation Admin reaches far past the desk).
      this.canViewPricingComparison = this.rolesInclude(roles, "Pricing Admin")
      this.canViewLocks = this.canViewRepricedLocks || this.canViewRevenueAnalysis || this.canViewPricingComparison
      this.canViewHmda = this.rolesInclude(roles, "Corp") || this.rolesInclude(roles, "HMDA")
      this.canViewAccounting = this.rolesInclude(roles, "Corp") || this.rolesInclude(roles, "Accounting")
      // swiftAnalyze and PA Import. Was never set here, so neither link showed in top-bar mode.
      // Mirrors navConfig.js computeNavPermissions().
      this.isAdmin = this.rolesInclude(roles, "Admin")
    },
    async stopImpersonate() {
      await stopImpersonation() // reloads the app as the signed-in user
    },
    showuserslist() {
      this.pickAUser = true
    },
    async getLar() {
      this.larLoaded = false
      try {
        const cached = localStorage.getItem("user_azure_data")
        if (cached) {
          const parsed = JSON.parse(cached)
          this.lar = parsed?.onPremisesExtensionAttributes?.extensionAttribute1 ?? null
          this.larLoaded = this.lar !== null
          return
        }

        const result = await apiService.GetUsersAzureData(this.azureId, this.token.accessToken)
        this.lar = result?.data?.data?.onPremisesExtensionAttributes?.extensionAttribute1 ?? null
        this.larLoaded = this.lar !== null
      } catch {
        this.lar = null
        this.larLoaded = false
      }
    },
    // Intercept the New Application click — show picker instead of going direct
    onNewAppClick() {
      this.showAppTypePicker = true
    },
    onSelectRegularApp() {
      this.showAppTypePicker = false
      window.open(`https://apply.loanlaunch.com/application?lar=${this.lar}`, "_blank")
    },
    onSelectReverseMortgage() {
      this.showAppTypePicker = false
      const base = window.location.origin + (process.env.BASE_URL || "/")
      window.open(`${base}reverse-mortgage`, "_blank")
    },
    onSelectCommercialLoan() {
      this.showAppTypePicker = false
      const base = window.location.origin + (process.env.BASE_URL || "/")
      window.open(`${base}application-commercial`, "_blank")
    },
    // "For Another Loan Officer" — pick a user, then open the standard
    // application link with THEIR lar (extensionAttribute1) instead of ours.
    async onPickLoanOfficerApp() {
      this.showAppTypePicker = false
      this.pickLoanOfficer = true
      await this.getLoanOfficerUsers()
    },
    onCloseLoanOfficerPicker() {
      if (this.launchingLoanOfficerApp) return
      this.pickLoanOfficer = false
      this.clearLoanOfficerDialogState()
    },
    clearLoanOfficerDialogState() {
      this.selectedLoanOfficer = null
      this.filteredLoanOfficers = [...this.loanOfficerUsers]
      this.loanOfficersLoading = false
    },
    async getLoanOfficerUsers() {
      this.loanOfficersLoading = true
      return apiService
        .GetUsersSelectsActiveOnly(this.azureId, this.token.accessToken)
        .then(res => {
          if (res.data?.data) {
            const sorted = [...res.data.data].sort((a, b) =>
              a.displayName.localeCompare(b.displayName)
            )
            this.loanOfficerUsers = sorted
            this.filteredLoanOfficers = sorted
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loanOfficersLoading = false
        })
    },
    onLoanOfficerFilterChange(e) {
      this.loanOfficersLoading = true
      this.filteredLoanOfficers = filterBy(this.loanOfficerUsers, e.filter)
      this.loanOfficersLoading = false
    },
    onLoanOfficerChange(e) {
      if (this.launchingLoanOfficerApp) return
      const value = e.target.value
      this.selectedLoanOfficer = value

      const larOverride = value?.extensionAttribute1
      if (!larOverride) return

      this.launchingLoanOfficerApp = true
      window.open(`https://apply.loanlaunch.com/application?lar=${larOverride}`, "_blank")

      this.pickLoanOfficer = false
      this.launchingLoanOfficerApp = false
      this.clearLoanOfficerDialogState()
    },
    closegsai() {
      this.showAi = false
    },
    async deepLogout() {
      if (getActAs()) await stopImpersonation({ reload: false })
      auth.logout()
    }
  }
}
</script>

<style scoped>
.small-header{
  font-size: 10px;
}
.app-header {
  border-bottom: 0;
  background: var(--background);
  color: var(--on-background);
  position: relative;
  z-index: 20;
  box-sizing: border-box;
  font-family: var(--bs-font-sans-serif);
  -webkit-font-smoothing: antialiased;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem 0;
  padding-bottom: 0;
  min-width: 100%;
  background: var(--background);
  color: var(--on-background);
}

.brand-logo {
  width: 100%;
  max-width: 250px;
  height: 37.58px;
  padding-left: 15px;
}

.header-actions {
  display: flex;
  align-items: center;
  font-size: 1.8rem;
}

.new-app-btn {
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.14);
  background: rgba(255, 255, 255, 0.9);
  color: var(--primary);
  font-weight: 600;
  font-size: 1.3rem;
  padding: 0 14px;
  height: 36px;
  cursor: pointer;
  line-height: 1;
}

.new-app-btn:hover {
  background: rgba(15, 23, 42, 0.05);
}

.new-app-btn:disabled {
  background: var(--kendo-button-bg);
  color: #999;
  cursor: not-allowed;
}

.header-nav {
  background: var(--background);
  border-bottom: 0.3rem solid var(--accent);
  padding: 1rem 1rem 0;
  padding-bottom: 5px;
}

.nav-links {
  display: flex;
  align-items: baseline;
  gap: 20px;
}

.nav-link {
  text-decoration: none;
  color: var(--primary);
  font-size: 1.6rem;
  font-weight: 400;
  line-height: 1.4285714286;
  letter-spacing: 0;
  padding: 6px 12px;
  margin: 0;
  font-family: Segoe UI;
}

.nav-link:hover {
  color: var(--text-secondary-on-background);
}

.hide-on-mobile {
  display: inline-flex;
}

/* ── Side layout (SideNav.vue active) ──
   Same header as GSDesign (connectGS): a white card floating above the page with rounded corners, a light border
   and a soft shadow, rather than a full-width bar. */
.app-header.layout-side {
  background: transparent;
}

.app-header.layout-side .header-top {
  min-width: 0;
  min-height: 60px;
  /* Same box as connectGS3.0 TopBar.vue (.topbar). The side gutter is --shell-gutter (App.vue)
     so the card's edges line up with the page content under it. */
  margin: 14px var(--shell-gutter, 28px) 0;
  padding: 8px 16px;
  background: var(--background);
  border: 1px solid #e7ebf3;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(5, 29, 58, 0.06), 0 1px 3px rgba(5, 29, 58, 0.05);
}

.app-header.layout-side .header-nav {
  display: none;           /* links live in the sidebar on desktop */
}

.header-page-slot {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1 1 auto;
}

/* Empty slot (route has no page-header block to teleport, e.g. Pipeline): collapse to nothing
   instead of leaving a gap where a title would be. */
.header-page-slot:empty {
  flex: 0 0 0;
}

/* Neutralize the boxed/full-width styling each view's own header block has in its normal spot
   (border, 100% width, page background, bottom margin) now that it's living in our top bar.
   Keyed on the class names of the page-header blocks we've wired up so far — extend this list
   the same way when another view's block gets teleported here. */
.header-page-slot :deep(.view-title-container),
.header-page-slot :deep(.dashboard-header),
.header-page-slot :deep(.page-header-left),
.header-page-slot :deep(.page-header > div:first-child) {
  width: auto;
  padding: 0;
  margin: 0;
  border-bottom: 0;
  background: transparent;
}

/* The plain-title views (LoanPriceSheet, Repriceapproval) wrap an <h1>/<p> with no page-icon —
   their h1/p carry their own margins meant for a full-width page body; tighten those up too. */
.header-page-slot :deep(h1) {
  margin: 0;
  font-size: 1.7rem;
}

.header-page-slot :deep(p) {
  margin: 2px 0 0;
  font-size: 1.15rem;
}

/* DashboardShell's title block (Prospect / Originate / Process ...) sized and colored like the
   connectGS3.0 / swiftLoan header: 38px blue tile, 18px title, 12.5px muted subtitle.
   Only applies once teleported into the top bar, so the old top layout is untouched. */
.header-page-slot :deep(.dashboard-title) {
  grid-template-columns: 38px minmax(0, 1fr);
  column-gap: 12px;
  align-items: center;
}
.header-page-slot :deep(.title-icon) {
  width: 38px;
  height: 38px;
  min-height: 38px;
  border-radius: 10px;
  background: #2f6fed;
}
.header-page-slot :deep(.title-icon i) {
  font-size: 17px;
}
.header-page-slot :deep(.title-select) {
  height: 24px;
  font-size: 18px;
  color: #16305a;
}
.header-page-slot :deep(.title-select-wrap::after) {
  border-color: #16305a;
}
.header-page-slot :deep(.subtitle) {
  margin-top: 2px;
  font-size: 12.5px;
  color: #8a95a8;
}

/* Same treatment for the shared page-header pattern most views use
   (.page-header-left > .page-icon + .page-title / .page-subtitle: My Profile, PCCD, Lock Desk ...). */
.header-page-slot :deep(.page-header-left) {
  gap: 12px;
}
.header-page-slot :deep(.page-icon) {
  width: 38px;
  height: 38px;
  min-width: 38px;
  border-radius: 10px;
  background: #2f6fed;
  font-size: 17px;
}
.header-page-slot :deep(.page-title) {
  font-size: 18px;
  line-height: 1.2;
  color: #16305a;
}
.header-page-slot :deep(.page-subtitle) {
  margin-top: 2px;
  font-size: 12.5px;
  color: #8a95a8;
}

@media (max-width: 630px) {
  /* Mobile fallback: sidebar is hidden, bring the horizontal nav back */
  .app-header.layout-side .header-nav {
    display: block;
  }
  .page-title-icon { display: none; }
  .page-title-main { font-size: 1.6rem; }
}

.cgsdark .app-header.layout-side .header-top {
  border-bottom-color: rgba(148, 163, 184, 0.15);
}

.cgsdark .page-title-main {
  color: rgba(226, 232, 240, 0.95);
}

.cgsdark .page-title-sub {
  color: rgba(148, 163, 184, 0.8);
}

.cgsdark .page-title-icon {
  background: rgba(19, 106, 211, 0.25);
}

/* ── Application Type Picker Modal ── */
.app-type-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(5, 29, 58, 0.45);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.app-type-modal {
  background: var(--background);
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 24px 60px rgba(5, 29, 58, 0.22);
  overflow: hidden;
}

.app-type-header {
  padding: 24px 24px 20px;
  position: relative;
  border-bottom: 1px solid var(--secondary-ltr);
}

.app-type-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--primary);
  margin: 0 0 4px;
  font-family: Segoe UI, sans-serif;
}

.app-type-subtitle {
  font-size: 1.3rem;
  color: var(--text-secondary-on-background);
  margin: 0;
  font-family: Segoe UI, sans-serif;
}

.app-type-close {
  position: absolute;
  top: 18px;
  right: 18px;
  background: none;
  border: none;
  font-size: 1.8rem;
  color: var(--text-secondary-on-background);
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.app-type-close:hover {
  background: var(--background-dk);
  color: var(--primary);
}

.app-type-options {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.app-type-card {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  border-radius: 12px;
  border: 1.5px solid var(--secondary-ltr);
  background: var(--background);
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: all 0.18s ease;
}

.app-type-card:hover {
  border-color: var(--accent);
  background: var(--accent-ltst);
  box-shadow: 0 4px 16px rgba(57, 139, 135, 0.12);
  transform: translateY(-1px);
}

.app-type-card:hover .app-type-arrow {
  transform: translateX(4px);
  color: var(--accent);
}

.app-type-icon {
  width: 46px;
  height: 46px;
  border-radius: 10px;
  background: var(--primary-ltr);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  flex-shrink: 0;
}

.app-type-icon.reverse {
  background: var(--accent-ltr);
  color: var(--accent-dk);
}

.app-type-icon.commercial {
  background: #eff1f5;
  color: #475569;
}

.app-type-icon.loanofficer {
  background: var(--accent-ltr);
  color: var(--accent-dk);
}

.app-type-info {
  flex: 1;
  min-width: 0;
}

.app-type-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary);
  margin: 0 0 3px;
  font-family: Segoe UI, sans-serif;
}

.app-type-desc {
  font-size: 1.25rem;
  color: var(--text-secondary-on-background);
  margin: 0;
  font-family: Segoe UI, sans-serif;
}

.app-type-arrow {
  font-size: 1.4rem;
  color: var(--text-hint-on-background);
  flex-shrink: 0;
  transition: transform 0.18s ease, color 0.18s ease;
}

/* Modal transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-active .app-type-modal,
.modal-fade-leave-active .app-type-modal {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .app-type-modal {
  transform: scale(0.95) translateY(8px);
}

.modal-fade-leave-to .app-type-modal {
  transform: scale(0.95) translateY(8px);
  opacity: 0;
}

/* Help modal */
.help-modal {
  position: relative;
  width: 1000px;
  max-width: 90vw;
  height: 600px;
  max-height: 75vh;
}

.help-frame {
  width: 100%;
  height: 100%;
  border-radius: 10px;
}

.iframe-loader {
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: 10px;
  background: var(--background);
  color: var(--primary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 1.4rem;
  font-weight: 600;
}

@media (max-width: 630px) {
  .app-header {
    --header-pad-x: 0.5rem;
  }

  .hide-on-mobile {
    display: none;
  }

  .new-app-btn {
    font-size: 1.2rem;
    padding: 0 12px;
  }

  .help-modal {
    width: 90vw;
    height: 55vh;
  }
}

/* Dark mode */
.cgsdark .app-header {
  background: var(--kendo-body-bg, #070b14);
}

/* ── Nav Dropdown ── */
.nav-dropdown {
  position: relative;
  align-items: baseline;
}

.nav-dropdown-trigger {
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.nav-chevron {
  font-size: 0.9rem;
  transition: transform 0.15s ease;
  opacity: 0.7;
}

.nav-dropdown:hover .nav-chevron {
  transform: rotate(180deg);
}

.nav-dropdown-menu {
  display: none;
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  background: var(--background);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 7px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  z-index: 500;
  padding: 8px 0 5px;
}

.nav-dropdown:hover .nav-dropdown-menu {
  display: block;
}

.nav-dropdown-item {
  display: block;
  padding: 9px 16px;
  text-decoration: none;
  color: var(--primary);
  font-size: 1.5rem;
  font-family: Segoe UI;
  font-weight: 400;
  white-space: nowrap;
  transition: background 0.12s ease;
}

.nav-dropdown-item:hover {
  background: rgba(0, 0, 0, 0.045);
  color: var(--text-secondary-on-background);
}

.nav-dropdown-item.router-link-active {
  color: var(--accent);
  font-weight: 600;
}

/* ── Nested submenu (flyout) — used by swiftComply groups ── */
.nav-submenu {
  position: relative;
}

.nav-submenu-trigger {
  cursor: pointer;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.nav-submenu-chevron {
  font-size: 0.9rem;
  opacity: 0.6;
  transition: transform 0.15s ease;
}

.nav-submenu:hover .nav-submenu-chevron {
  transform: translateX(2px);
}

.nav-submenu-menu {
  display: none;
  position: absolute;
  top: -8px;               /* aligns flyout top with parent item */
  left: 100%;
  min-width: 200px;
  background: var(--background);
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 7px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  z-index: 510;
  padding: 8px 0 5px;
}

.nav-submenu:hover .nav-submenu-menu {
  display: block;
}

.nav-submenu:hover > .nav-submenu-trigger {
  background: rgba(0, 0, 0, 0.045);
  color: var(--text-secondary-on-background);
}

.cgsdark .header-nav {
  background: var(--kendo-body-bg, #070b14);
}

.cgsdark .nav-link {
  color: rgba(226, 232, 240, 0.9);
}

.cgsdark .nav-link:hover {
  color: rgba(226, 232, 240, 0.98);
}

.cgsdark .nav-link.router-link-active {
  color: color-mix(in srgb, var(--accent) 70%, rgba(226, 232, 240, 0.95));
}

.cgsdark .new-app-btn {
  background: rgba(2, 6, 23, 0.55);
  border-color: rgba(148, 163, 184, 0.22);
  color: rgba(226, 232, 240, 0.9);
}

.cgsdark .new-app-btn:hover {
  background: rgba(226, 232, 240, 0.06);
}

.cgsdark .nav-dropdown-menu {
  background: var(--kendo-body-bg, #070b14);
  border-color: rgba(226, 232, 240, 0.12);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
}

.cgsdark .nav-dropdown-item {
  color: rgba(226, 232, 240, 0.9);
}

.cgsdark .nav-dropdown-item:hover {
  background: rgba(226, 232, 240, 0.06);
  color: rgba(226, 232, 240, 0.98);
}

.cgsdark .nav-dropdown-item.router-link-active {
  color: color-mix(in srgb, var(--accent) 70%, rgba(226, 232, 240, 0.95));
}

.cgsdark .app-type-overlay {
  background: rgba(2, 6, 23, 0.7);
}

.cgsdark .app-type-modal {
  background: var(--kendo-body-bg, #070b14);
  border: 1px solid rgba(148, 163, 184, 0.15);
}

.cgsdark .app-type-header {
  border-bottom-color: rgba(148, 163, 184, 0.15);
}

.cgsdark .app-type-title {
  color: rgba(226, 232, 240, 0.95);
}

.cgsdark .app-type-subtitle {
  color: rgba(148, 163, 184, 0.8);
}

.cgsdark .app-type-close {
  color: rgba(148, 163, 184, 0.7);
}

.cgsdark .app-type-close:hover {
  background: rgba(226, 232, 240, 0.06);
  color: rgba(226, 232, 240, 0.9);
}

.cgsdark .app-type-card {
  background: rgba(226, 232, 240, 0.03);
  border-color: rgba(148, 163, 184, 0.18);
}

.cgsdark .app-type-card:hover {
  background: rgba(57, 139, 135, 0.12);
  border-color: var(--accent);
}

.cgsdark .app-type-name {
  color: rgba(226, 232, 240, 0.95);
}

.cgsdark .app-type-desc {
  color: rgba(148, 163, 184, 0.8);
}

.cgsdark .app-type-icon {
  background: rgba(19, 106, 211, 0.15);
  color: var(--primary-mid);
}

.cgsdark .app-type-icon.reverse {
  background: rgba(57, 139, 135, 0.15);
  color: var(--accent-lt);
}

.cgsdark .app-type-icon.commercial {
  background: rgba(148, 163, 184, 0.12);
  color: rgba(226, 232, 240, 0.8);
}

.cgsdark .app-type-icon.loanofficer {
  background: rgba(57, 139, 135, 0.15);
  color: var(--accent-lt);
}

.cgsdark .iframe-loader {
  background: var(--kendo-body-bg, #070b14);
  color: rgba(226, 232, 240, 0.9);
}
</style>